import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const BarcodeGenerator = () => {
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [codeType, setCodeType] = useState('barcode'); // barcode or qrcode
  const [stickerSize, setStickerSize] = useState('50x25'); // WxH in mm
  const [showPrice, setShowPrice] = useState(true);
  const [showMRP, setShowMRP] = useState(true);
  const [showName, setShowName] = useState(true);
  const [nameMaxChars, setNameMaxChars] = useState(20);
  const [copies, setCopies] = useState(1);
  const previewRef = useRef(null);

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const resp = await axios.get(`${API}/products/enhanced`, { headers: { Authorization: `Bearer ${token}` } });
      setProducts(resp.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.sku || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.barcode || '').toLowerCase().includes(search.toLowerCase())
  );

  const toggleProduct = (product) => {
    setSelectedProducts(prev => {
      const exists = prev.find(p => p.id === product.id);
      if (exists) return prev.filter(p => p.id !== product.id);
      return [...prev, { ...product, qty: copies }];
    });
  };

  const updateQty = (productId, qty) => {
    setSelectedProducts(prev => prev.map(p => p.id === productId ? { ...p, qty: parseInt(qty) || 1 } : p));
  };

  const selectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => ({ ...p, qty: copies })));
    }
  };

  const [w, h] = stickerSize.split('x').map(Number);

  const generateAndPrint = useCallback(async () => {
    const stickers = [];
    for (const product of selectedProducts) {
      for (let i = 0; i < (product.qty || 1); i++) {
        stickers.push(product);
      }
    }
    if (!stickers.length) { alert('Select products first'); return; }

    // Generate barcode/QR images
    const stickerHtmls = [];
    for (const product of stickers) {
      let codeImg = '';
      const codeValue = codeType === 'barcode'
        ? (product.barcode || product.sku || product.id)
        : (product.sku || product.id);

      if (codeType === 'barcode') {
        try {
          const canvas = document.createElement('canvas');
          JsBarcode(canvas, codeValue, {
            format: 'CODE128', width: 1.5, height: 30, displayValue: true,
            fontSize: 8, margin: 2, textMargin: 1
          });
          codeImg = canvas.toDataURL('image/png');
        } catch (e) {
          // Fallback
          const canvas = document.createElement('canvas');
          JsBarcode(canvas, codeValue.replace(/[^a-zA-Z0-9]/g, '').slice(0, 20) || 'NOCODE', {
            format: 'CODE128', width: 1.5, height: 30, displayValue: true,
            fontSize: 8, margin: 2
          });
          codeImg = canvas.toDataURL('image/png');
        }
      } else {
        try {
          codeImg = await QRCode.toDataURL(codeValue, { width: 80, margin: 1 });
        } catch (e) { console.error(e); }
      }

      const name = showName ? (product.name || '').slice(0, nameMaxChars) : '';
      const price = showPrice ? `Rs.${(product.selling_price || 0).toFixed(2)}` : '';
      const mrp = showMRP && product.mrp ? `MRP: Rs.${product.mrp.toFixed(2)}` : '';

      stickerHtmls.push(`
        <div class="sticker" style="width:${w}mm;height:${h}mm;">
          ${name ? `<div class="name">${name}</div>` : ''}
          <div class="code-area"><img src="${codeImg}" class="${codeType === 'barcode' ? 'barcode-img' : 'qr-img'}" /></div>
          <div class="price-row">
            ${price ? `<span class="price">${price}</span>` : ''}
            ${mrp ? `<span class="mrp">${mrp}</span>` : ''}
          </div>
        </div>
      `);
    }

    const printWindow = window.open('', '_blank', 'width=600,height=800');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Barcode Stickers</title>
    <style>
      * { margin:0; padding:0; box-sizing:border-box; }
      @page { margin: 2mm; }
      body { font-family: Arial, sans-serif; }
      .sticker-grid { display:flex; flex-wrap:wrap; }
      .sticker {
        border: 0.5px dashed #ccc; display:flex; flex-direction:column;
        align-items:center; justify-content:center; padding:1mm; overflow:hidden;
        page-break-inside:avoid;
      }
      .name { font-size:7px; font-weight:bold; text-align:center; max-width:100%; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; line-height:1.2; }
      .code-area { flex:1; display:flex; align-items:center; justify-content:center; }
      .barcode-img { max-width:100%; max-height:${Math.max(h - 12, 10)}mm; }
      .qr-img { max-width:${Math.min(w - 4, h - 10)}mm; max-height:${Math.min(w - 4, h - 10)}mm; }
      .price-row { display:flex; gap:2mm; font-size:7px; font-weight:bold; }
      .price { color:#000; }
      .mrp { color:#666; text-decoration:line-through; }
      @media print { .sticker { border:none; } }
    </style></head><body>
      <div class="sticker-grid">${stickerHtmls.join('')}</div>
    </body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 500);
  }, [selectedProducts, codeType, w, h, showPrice, showMRP, showName, nameMaxChars]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div></div>;

  return (
    <div data-testid="barcode-generator-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Barcode / QR Code Generator</h1>
          <p className="text-slate-500">Generate and print barcode/QR stickers for products</p>
        </div>
        <button onClick={generateAndPrint} disabled={selectedProducts.length === 0}
          className="bg-gradient-to-r from-violet-600 to-purple-600 text-white px-6 py-2 rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 flex items-center gap-2"
          data-testid="print-stickers-btn">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
          Print ({selectedProducts.reduce((s, p) => s + (p.qty || 1), 0)} stickers)
        </button>
      </div>

      {/* Settings */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Sticker Settings</h2>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Code Type</label>
            <select value={codeType} onChange={e => setCodeType(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="code-type-select">
              <option value="barcode">Barcode</option>
              <option value="qrcode">QR Code</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Sticker Size (mm)</label>
            <select value={stickerSize} onChange={e => setStickerSize(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="sticker-size-select">
              <option value="50x25">50 x 25 mm</option>
              <option value="50x30">50 x 30 mm</option>
              <option value="38x25">38 x 25 mm</option>
              <option value="60x30">60 x 30 mm</option>
              <option value="60x40">60 x 40 mm</option>
              <option value="70x40">70 x 40 mm</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Name Max Chars</label>
            <input type="number" value={nameMaxChars} onChange={e => setNameMaxChars(parseInt(e.target.value) || 20)} min={5} max={50} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Copies Each</label>
            <input type="number" value={copies} onChange={e => setCopies(parseInt(e.target.value) || 1)} min={1} max={100} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="copies-input" />
          </div>
          <div className="flex items-end gap-3">
            <label className="flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={showName} onChange={e => setShowName(e.target.checked)} className="w-4 h-4 text-violet-600 rounded" /> Name
            </label>
            <label className="flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={showPrice} onChange={e => setShowPrice(e.target.checked)} className="w-4 h-4 text-violet-600 rounded" /> Price
            </label>
            <label className="flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={showMRP} onChange={e => setShowMRP(e.target.checked)} className="w-4 h-4 text-violet-600 rounded" /> MRP
            </label>
          </div>
        </div>
      </div>

      {/* Product Selection */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-3 mb-4">
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search products by name, SKU or barcode..."
            className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            data-testid="product-search-barcode" />
          <button onClick={selectAll} className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50" data-testid="select-all-btn">
            {selectedProducts.length === filteredProducts.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>

        <div className="max-h-96 overflow-y-auto space-y-1">
          {filteredProducts.map(product => {
            const isSelected = selectedProducts.find(p => p.id === product.id);
            return (
              <div key={product.id}
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${isSelected ? 'bg-violet-50 border border-violet-300' : 'hover:bg-slate-50 border border-transparent'}`}
                onClick={() => toggleProduct(product)}
                data-testid={`product-row-${product.id}`}
              >
                <input type="checkbox" checked={!!isSelected} readOnly className="w-4 h-4 text-violet-600 rounded pointer-events-none" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-800 text-sm truncate">{product.name}</p>
                  <p className="text-xs text-slate-500">
                    SKU: {product.sku || '-'}
                    {product.barcode && ` | Barcode: ${product.barcode}`}
                    {` | Price: Rs.${(product.selling_price || 0).toFixed(2)}`}
                    {product.mrp ? ` | MRP: Rs.${product.mrp.toFixed(2)}` : ''}
                  </p>
                </div>
                {isSelected && (
                  <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                    <label className="text-xs text-slate-500">Qty:</label>
                    <input type="number" value={isSelected.qty || 1} min={1} max={999}
                      onChange={e => updateQty(product.id, e.target.value)}
                      className="w-16 px-2 py-1 border border-slate-200 rounded text-sm text-center"
                      data-testid={`sticker-qty-${product.id}`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {selectedProducts.length > 0 && (
        <div className="bg-slate-100 rounded-xl p-3 text-center text-sm text-slate-600">
          {selectedProducts.length} products selected | {selectedProducts.reduce((s, p) => s + (p.qty || 1), 0)} total stickers
        </div>
      )}
    </div>
  );
};

export default BarcodeGenerator;
